import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { useTheme } from '../../../contexts/ThemeContext';
import { getUroSDK, getUroSocket, listUnifiedJobs } from '../../../services/URO';
import DefaultPresentation from './components/DefaultPresentation';
import { JobDetailModal } from '../../../components/JobDetailView';
import useResponsive from '../../../utils/useResponsive';

const PRIMARY = '#FA8B14';

/**
 * CreateScreen - Unified entry point for both models and workflows
 * 
 * Acts as a logic gate:
 * 1. Fetches item by ID (tries workflow first, falls back to model)
 * 2. Determines presentation style based on item type/parameters
 * 3. Renders appropriate presentation component
 */
export default function CreateScreen() {
  const navigate = useNavigate();
  const { id } = useParams(); // Generic ID - could be workflow or model
  const { colors } = useTheme();
  const { openCreateModal } = useOutletContext() || {};
  const { isMobile } = useResponsive();
  
  // Item state - unified structure for both models and workflows
  const [item, setItem] = useState(null);
  const [itemType, setItemType] = useState(null); // 'workflow' | 'model'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examples, setExamples] = useState([]);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [result, setResult] = useState(null);
  const currentJobIdRef = useRef(null);
  
  // Socket for real-time updates
  const socketRef = useRef(null);

  // Fetch item on mount - try workflow first, then model
  // Also re-run if screen size changes (for desktop redirect)
  useEffect(() => {
    fetchItem();
    setupSocket();
    
    return () => cleanupSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isMobile]);

  const setupSocket = () => {
    const socket = getUroSocket();
    socketRef.current = socket;
    
    const getJobId = (data) => data.image_job_id || data.workflow_job_id;
    
    const onCreated = (data) => {
      const jobId = getJobId(data);
      if (jobId === currentJobIdRef.current) {
        setJobStatus('pending');
      }
    };
    
    const onProcessing = (data) => {
      const jobId = getJobId(data);
      if (jobId === currentJobIdRef.current) {
        setJobStatus('processing');
      }
    };
    
    const onComplete = async (data) => {
      const completedJobId = data.image_job_id || data.workflow_job_id;
      if (completedJobId !== currentJobIdRef.current) return;
      
      // Extract outputs
      let outputs = [];
      if (data.results && Array.isArray(data.results)) {
        outputs = data.results.map(r => ({
          url: r.external_url || r.url || null,
          content: r.content || null,
          type: detectContentType(r)
        })).filter(o => o.url || o.content);
      }
      
      // Fetch complete job data
      try {
        const jobsResult = await listUnifiedJobs({ 
          page: 1, 
          limit: 1,
          ...(data.workflow_job_id ? { workflow_job_id: data.workflow_job_id } : { image_job_id: data.image_job_id })
        });
        
        const fullJobData = jobsResult?.data?.items?.[0];
        if (fullJobData) {
          setResult(fullJobData);
        } else {
          setResult({
            ...(data.workflow_job_id ? { workflow_job_id: data.workflow_job_id } : { image_job_id: data.image_job_id }),
            output_results: outputs,
            cost: data.cost_to_client,
          });
        }
      } catch (err) {
        setResult({
          ...(data.workflow_job_id ? { workflow_job_id: data.workflow_job_id } : { image_job_id: data.image_job_id }),
          output_results: outputs,
          cost: data.cost_to_client,
        });
      }
      
      setJobStatus('completed');
      setGenerating(false);
    };
    
    const onError = (data) => {
      const jobId = getJobId(data);
      if (jobId === currentJobIdRef.current) {
        setError(data.error || data.failed_message || 'Generation failed');
        setJobStatus('failed');
        setGenerating(false);
      }
    };
    
    // Listen for both model and workflow events
    socket.on('job:created', onCreated);
    socket.on('job:processing', onProcessing);
    socket.on('job:complete', onComplete);
    socket.on('job:error', onError);
    socket.on('workflow:created', onCreated);
    socket.on('workflow:processing', onProcessing);
    socket.on('workflow:complete', onComplete);
    socket.on('workflow:failed', onError);
    socket.on('workflow:error', onError);
  };

  const cleanupSocket = () => {
    const socket = socketRef.current;
    if (socket && socket.off) {
      socket.off('job:created');
      socket.off('job:processing');
      socket.off('job:complete');
      socket.off('job:error');
      socket.off('workflow:created');
      socket.off('workflow:processing');
      socket.off('workflow:complete');
      socket.off('workflow:failed');
      socket.off('workflow:error');
    }
  };

  const detectContentType = (item) => {
    if (item.content_type) return item.content_type;
    const url = item.external_url || item.url || '';
    if (url.match(/\.(mp4|webm|mov|avi|mkv)/i)) return 'video';
    if (url.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)/i)) return 'image';
    if (typeof item.content === 'string' && !url) return 'text';
    return 'image';
  };

  const fetchItem = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const sdk = getUroSDK();
      
      // Try workflow first
      try {
        const workflowRes = await sdk.getWorkflowById(id);
        // SDK returns { data: workflow } format
        const workflow = workflowRes?.data || workflowRes;
        
        if (workflow && workflow.workflow_definition_id) {
          // On desktop/tablet, redirect to Zen screen
          if (!isMobile) {
            navigate(`/zen/workflow/${workflow.workflow_definition_id}`, { replace: true });
            return;
          }
          setItem(normalizeWorkflow(workflow));
          setItemType('workflow');
          setExamples([]);
          return;
        }
      } catch {
        // Workflow not found, will try model below
      }
      
      // Try model
      try {
        const modelRes = await sdk.getModelById(id);
        // SDK returns { data: model } format
        const model = modelRes?.data || modelRes;
        
        if (model && model.model_id) {
          // On desktop/tablet, redirect to Zen screen
          if (!isMobile) {
            navigate(`/zen/model/${model.model_id}`, { replace: true });
            return;
          }
          setItem(normalizeModel(model));
          setItemType('model');
          setExamples([]);
          return;
        }
      } catch {
        // Model not found either
      }
      
      // Neither found
      setError('Item not found');
    } catch (err) {
      console.error('[CreateScreen] Fetch failed:', err);
      setError('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Normalize workflow to unified item structure
  const normalizeWorkflow = (workflow) => ({
    id: workflow.workflow_definition_id,
    name: workflow.name,
    description: workflow.description,
    type: 'workflow',
    parameters: workflow.parameters || [],
    videoUrl: workflow.workflow_video_url,
    coverImage: workflow.workflow_cover_image_url,
    cover2Image: workflow.workflow_cover_2_image_url,
    thumbnailImage: null, // workflow_definition doesn't have thumbnail column
    averageCost: workflow.average_cost,
    averageEta: workflow.average_eta,
    warningMessage: workflow.warning_message,
    resolution: workflow.resolution,
    raw: workflow,
  });

  // Normalize model to unified item structure
  const normalizeModel = (model) => ({
    id: model.model_id,
    name: model.model_name,
    description: model.model_description,
    type: 'model',
    parameters: model.parameters || [],
    videoUrl: model.model_video_url,
    coverImage: model.model_cover_image_url,
    cover2Image: model.model_cover_2_image_url,
    thumbnailImage: model.model_thumbnail_image_url,
    averageCost: model.average_cost,
    averageEta: model.average_eta,
    warningMessage: model.warning_message,
    resolution: model.resolution,
    raw: model,
  });

  // Handle generation
  const handleGenerate = async (params) => {
    try {
      setGenerating(true);
      setJobStatus('pending');
      setError(null);
      setResult(null);

      let response;
      
      if (itemType === 'workflow') {
        response = await getUroSDK().runWorkflow({
          workflow_definition_id: id,
          input_params: params
        });
      } else {
        response = await getUroSDK().runModel({
          model_id: id,
          ...params
        });
      }
      
      const jobId = response?.data?.workflow_job_id || response?.data?.image_job_id || 
                    response?.workflow_job_id || response?.image_job_id;
      
      const responseStatus = response?.data?.status || response?.status;
      const responseError = response?.data?.error || response?.error;
      
      if (responseStatus === 'failed') {
        setError(responseError || 'Generation failed');
        setGenerating(false);
        setJobStatus('failed');
        return;
      }

      if (jobId) {
        currentJobIdRef.current = jobId;
        setJobStatus('processing');
      } else {
        throw new Error('No job ID returned');
      }
    } catch (err) {
      console.error('[CreateScreen] Generation failed:', err);
      const errorMessage = err?.response?.data?.error?.message ||
                          err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          err?.message || 
                          'Request failed - please try again';
      setError(errorMessage);
      setGenerating(false);
      setJobStatus(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.background,
      }}>
        <div style={{ textAlign: 'center', color: '#9ca3af' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" />
          </svg>
          <div style={{ marginTop: 12, fontSize: 14 }}>Loading...</div>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state (only if no result to show)
  if (error && !result && !generating) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.background,
        padding: 20,
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <div style={{ fontSize: 16, color: '#ef4444', marginBottom: 8, textAlign: 'center' }}>{error}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 20px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: '#fff',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Render
  return (
    <>
      {/* Result Modal */}
      <JobDetailModal
        visible={!!result}
        data={result}
        jobId={result?.workflow_job_id || result?.image_job_id}
        onClose={() => setResult(null)}
        onReuseParams={() => setResult(null)}
      />
      
      {/* Presentation Component */}
      <DefaultPresentation
        item={item}
        itemType={itemType}
        examples={examples}
        generating={generating}
        jobStatus={jobStatus}
        error={error}
        onGenerate={handleGenerate}
        onBack={() => navigate(-1)}
        onChangeModel={openCreateModal}
        colors={colors}
      />
    </>
  );
}
